import {decodeProtectedHeader, type ProtectedHeaderParameters} from "jose/decode/protected_header";
import {debug, warn} from "../utils/log";
import {apiFetch} from "../utils/api";
import type {ValidationResult} from "../types";
import {API_PREFIX} from "../utils/constants";
import {getSignKeyPublic} from "../utils/crypto";
import {getNewSolution} from "./solution";
import {TranslatableError} from "../utils/error";
import {get, set} from "../utils/storage";

function getSolutionMetadata(solution: string): ProtectedHeaderParameters {
    if (solution) {
        try {
            return decodeProtectedHeader(JSON.parse(solution));
        } catch {
        }
    }

    return {}
}

const LAST_FALSE_IS_FRESH_RESULT = `is-fresh`;

async function storeLastFalseIsFreshResult(lastFalseResult: number) {
    try {
        await set(LAST_FALSE_IS_FRESH_RESULT, lastFalseResult);
    } catch {
        warn(`Failed to store freshness`, {lastFalseResult});
    }
}

export async function isFresh(solution: string) {
    const metadata = getSolutionMetadata(solution)

    let lastFalseResult: number | undefined;
    try {
        lastFalseResult = (await get(LAST_FALSE_IS_FRESH_RESULT)) || null;
        if (typeof lastFalseResult !== 'number' || Number.isNaN(lastFalseResult)) {
            lastFalseResult = -Infinity
        }
    } catch {
        // ignore errors
        lastFalseResult = -Infinity
    }
    const tenMinutesAgo = Date.now() - (10 * 60 * 1000);
    if (lastFalseResult < tenMinutesAgo) {
        await storeLastFalseIsFreshResult(Date.now());
        return false;
    }

    if (metadata.nbf && metadata.exp) {
        if (+metadata.nbf > Date.now() / 1000) {
            await storeLastFalseIsFreshResult(Date.now());
            return false;
        }
        if (+metadata.exp < (Date.now() / 1000) - 120) {
            await storeLastFalseIsFreshResult(Date.now());
            return false;
        }

        debug(`Solution metadata is fresh: nbf=${metadata.nbf}, exp=${metadata.exp}`);
        if (Math.random() < 0.25) { // 25% chance to randomly validate
            debug(`Solution metadata is fresh, but randomly decided to validate.`);
            await storeLastFalseIsFreshResult(Date.now());
            return false; // randomly decide to validate
        }
        await storeLastFalseIsFreshResult(lastFalseResult);
        return true;
    }
    await storeLastFalseIsFreshResult(Date.now());
    return false
}

export async function markSolutionAsNotFresh() {
    debug(`Marking solution as not fresh.`);
    await storeLastFalseIsFreshResult(-Infinity);
}

type UpdateFunction = ((up: {
    rate: number
}) => void);

export async function validate(solution: string, signal: AbortSignal, update: UpdateFunction): Promise<string> {
    if (await isFresh(solution)) {
        debug(`Looks like POW is fresh, skipping validation.`);
        return solution;
    }
    const validation = await apiFetch<ValidationResult>(API_PREFIX + "pow/validate", {
        body: {
            solution: solution,
            publicSignKey: await getSignKeyPublic(),
        },
        signal: signal
    });
    if (validation.rejected) {
        throw new TranslatableError({id: 'rejected'})
    }
    if (validation.valid) {
        debug(`Validation passed.`);
        return solution;
    }
    debug(`Validation failed or no solution provided. Proceeding to obtain a new solution.`);
    update({rate: NaN});
    const newSolution = await getNewSolution((rate) => update({rate}), signal);
    if (!newSolution) {
        throw new TranslatableError({id: 'failedToLoadSeal'})
    }
    debug(`New sealed solution obtained: ${newSolution}`);
    return newSolution;
}
